import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faChevronRight, faChevronDown, faLightbulb,
  faMoneyBillWave, faWallet, faUniversity, faExchangeAlt,
  faUser, faGraduationCap, faFileInvoice, faHandshake,
  faPlus, faSearch, faEye, faEdit, faCheck, faDollarSign,
  faFileCsv, faCalendarAlt, faTag, faClock, faUserCheck,
  faPrint, faDownload, faCreditCard, faBuilding,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';

const AdminTutoComptable = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'caisse-depenses',
      icon: faMoneyBillWave,
      title: '1.1 Caisse et Dépenses',
      color: '#10b981',
      intro: 'La section Caisse permet d\'effectuer rapidement des mouvements de sortie et d\'entrée d\'argent, sans passer par une vente ou un paiement de dépense.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Dans le menu latéral gauche, cliquez sur "Caisse et Dépenses" pour accéder à la gestion des mouvements de caisse.',
          tip: '💡 Astuce : Cette section est le point d\'entrée principal pour toutes les opérations de trésorerie.'
        },
        {
          step: 'b',
          title: 'Ajout d\'un mouvement',
          description: 'Cliquez sur "Ajouter un mouvement" pour ouvrir un formulaire modal avec les champs suivants :',
          details: [
            '🔄 Type (Entrée ou Sortie)',
            '💰 Montant de l\'opération',
            '📝 Description détaillée du mouvement'
          ],
          tip: '💡 Astuce : Utilisez des descriptions précises pour faciliter le suivi et le contrôle des opérations.'
        },
        {
          step: 'c',
          title: 'Exportation CSV',
          description: 'Cliquez sur "Exporter CSV" pour télécharger l\'intégralité des mouvements au format CSV.',
          tip: '💡 Astuce : Le fichier CSV peut être ouvert avec Excel pour une analyse approfondie.'
        }
      ]
    },
    {
      id: 'depenses',
      icon: faWallet,
      title: '1.2 Gestion des Dépenses',
      color: '#ef4444',
      intro: 'La section Dépense permet d\'effectuer une sortie d\'argent liée à une dépense précise. Chaque dépense enregistrée crée automatiquement un mouvement de sortie dans la caisse.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section Dépenses',
          description: 'Cliquez sur l\'onglet "Dépense" dans le menu horizontal pour accéder à la gestion des dépenses.',
          tip: '💡 Astuce : Les dépenses sont catégorisées pour un meilleur suivi budgétaire.'
        },
        {
          step: 'b',
          title: 'Ajout d\'une dépense',
          description: 'Cliquez sur "Ajouter" pour ouvrir un formulaire modal avec les champs suivants :',
          details: [
            '📝 Description de la dépense',
            '🏷️ Catégorie (ex: Fournitures, Transport, Communication)',
            '💰 Montant de la dépense',
            '📅 Date de la dépense',
            '👤 Payé par (personne ayant effectué le paiement)',
            '📊 Statut (En attente, Validée, Refusée)'
          ],
          tip: '💡 Astuce : Validez les dépenses uniquement après vérification des justificatifs.'
        },
        {
          step: 'c',
          title: 'Recherche de dépenses',
          description: 'Utilisez la barre de recherche pour filtrer les dépenses par :',
          details: [
            '📝 Description',
            '🏷️ Catégorie',
            '📊 Statut'
          ],
          tip: '💡 Astuce : La recherche est instantanée et s\'effectue en temps réel.'
        }
      ]
    },
    {
      id: 'caisse-bank',
      icon: faUniversity,
      title: '1.3 Caisse Bank',
      color: '#3b82f6',
      intro: 'La section Caisse Bank permet d\'effectuer des transactions entre la caisse et la banque, créant automatiquement des mouvements de caisse. Cette fonctionnalité nécessite la création préalable d\'un compte bancaire.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Caisse Bank" dans le menu latéral pour accéder à la gestion des comptes bancaires.',
          tip: '💡 Astuce : La création d\'un compte bancaire est indispensable avant toute transaction.'
        },
        {
          step: 'b',
          title: 'Ajout d\'un compte bancaire',
          description: 'Cliquez sur "Ajouter" pour ouvrir un formulaire modal avec les champs suivants :',
          details: [
            '🏦 Nom du compte (ex: Compte courant)',
            '🏛️ Banque (nom de l\'établissement)',
            '🔢 Numéro de compte (IBAN)',
            '💰 Solde initial'
          ],
          tip: '💡 Astuce : Vérifiez bien le numéro de compte avant validation.'
        }
      ]
    },
    {
      id: 'operation-caisse',
      icon: faExchangeAlt,
      title: '1.4 Opération Caisse',
      color: '#8b5cf6',
      intro: 'Cette section permet de visualiser les soldes de la caisse et de la banque, et d\'effectuer des transferts d\'argent entre les deux.',
      steps: [
        {
          step: 'a',
          title: 'Accès aux opérations',
          description: 'Cliquez sur l\'onglet "Opération Caisse" dans le menu horizontal pour afficher les soldes de la caisse et de la banque.',
          tip: '💡 Astuce : Consultez régulièrement les soldes pour une meilleure gestion de trésorerie.'
        },
        {
          step: 'b',
          title: 'Transfert Caisse → Banque',
          description: 'Activez le bouton "Caisse → Banque", puis :',
          details: [
            '🏦 Sélectionnez le compte bancaire destinataire',
            '💰 Saisissez le montant à transférer',
            '📝 Ajoutez une description (optionnelle)'
          ],
          tip: '💡 Astuce : Ce transfert crée automatiquement un mouvement de sortie dans la caisse.'
        },
        {
          step: 'c',
          title: 'Transfert Banque → Caisse',
          description: 'Activez le bouton "Banque → Caisse", puis :',
          details: [
            '🏦 Sélectionnez le compte bancaire source',
            '💰 Saisissez le montant à transférer',
            '📝 Ajoutez une description (optionnelle)'
          ],
          tip: '💡 Astuce : Ce transfert crée automatiquement un mouvement d\'entrée dans la caisse.'
        }
      ]
    },
    {
      id: 'paiements-agents',
      icon: faUser,
      title: '1.5 Paiements d\'Agents',
      color: '#f59e0b',
      intro: 'La section Paiements d\'agents permet de gérer les salaires et les avances sur salaire. Chaque paiement enregistré crée automatiquement un mouvement de sortie dans la caisse.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Paiements d\'agents" dans le menu latéral pour accéder à la gestion des salaires.',
          tip: '💡 Astuce : Cette section centralise tous les paiements liés au personnel.'
        },
        {
          step: 'b',
          title: 'Accès au paye rôle',
          description: 'Cliquez sur le bouton avec l\'icône d\'une main qui porte un dollar pour accéder au paye rôle de l\'agent.',
          tip: '💡 Astuce : Vérifiez les informations avant de procéder au paiement.'
        },
        {
          step: 'c',
          title: 'Génération de la fiche de paie',
          description: 'Cliquez sur "Générer fiche du mois" pour enregistrer le paiement et créer la fiche de paie.',
          tip: '💡 Astuce : La fiche de paie est générée automatiquement en PDF.'
        },
        {
          step: 'd',
          title: 'Validation du paiement',
          description: 'Cliquez sur le bouton avec l\'icône "check" (✓) dans la colonne Action du tableau pour ouvrir un modal de validation.',
          tip: '💡 Astuce : La validation est obligatoire pour finaliser le paiement.'
        },
        {
          step: 'e',
          title: 'Visualisation de la fiche de paie',
          description: 'Cliquez sur le bouton avec l\'icône "œil" (👁️) dans la colonne Action pour ouvrir un modal affichant la fiche de paie.',
          tip: '💡 Astuce : Vous pouvez imprimer ou télécharger la fiche de paie depuis le modal.'
        }
      ]
    },
    {
      id: 'ventes-formations',
      icon: faGraduationCap,
      title: '1.6 Ventes Formations',
      color: '#06b6d4',
      intro: 'La section Ventes Formations permet d\'enregistrer les ventes de formations. Chaque vente enregistrée crée automatiquement un mouvement d\'entrée dans la caisse.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur l\'onglet "Ventes formation" dans le menu horizontal pour accéder au formulaire de vente.',
          tip: '💡 Astuce : Les formations doivent être préalablement créées dans le module Formation.'
        },
        {
          step: 'b',
          title: 'Enregistrement d\'une vente',
          description: 'Remplissez le formulaire de vente avec les informations suivantes :',
          details: [
            '📚 Formation (sélectionnez dans la liste)',
            '👤 Nom complet du client',
            '📧 Email du client',
            '📱 Téléphone du client',
            '📍 Adresse du client'
          ],
          tip: '💡 Astuce : Vérifiez les coordonnées du client avant validation.'
        }
      ]
    },
    {
      id: 'gestion-factures',
      icon: faFileInvoice,
      title: '1.7 Gestion des Factures',
      color: '#ec4899',
      intro: 'La section Gestion des Factures permet de gérer les factures des clients ayant acheté des formations.',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Gestion des Factures" dans le menu horizontal pour accéder à la liste des factures.',
          tip: '💡 Astuce : Les factures sont générées automatiquement après une vente.'
        },
        {
          step: 'b',
          title: 'Recherche de clients',
          description: 'Utilisez la barre de recherche pour trouver le dernier client ayant payé une formation.',
          tip: '💡 Astuce : La recherche s\'effectue par nom, email ou téléphone.'
        },
        {
          step: 'c',
          title: 'Validation de l\'achat',
          description: 'Cliquez sur le bouton avec l\'icône stylo (✏️) pour valider l\'achat du client.',
          tip: '💡 Astuce : La validation déclenche l\'envoi de la facture par email.'
        },
        {
          step: 'd',
          title: 'Consultation de la facture',
          description: 'Cliquez sur le bouton avec l\'icône œil (👁️) pour ouvrir un modal et télécharger la facture en PDF.',
          tip: '💡 Astuce : La facture est formatée professionnellement avec le logo de votre entreprise.'
        }
      ]
    },
    {
      id: 'inscription-client',
      icon: faUserCheck,
      title: '1.8 Inscription du Client',
      color: '#8b5cf6',
      intro: 'Cette section permet d\'inscrire un client à une session de formation après son achat.',
      steps: [
        {
          step: 'a',
          title: 'Accès au profil client',
          description: 'Cliquez sur le bouton avec l\'icône utilisateur ou sur le nom du client pour entrer dans son profil.',
          tip: '💡 Astuce : Le profil client contient l\'historique de tous ses achats.'
        },
        {
          step: 'b',
          title: 'Inscription à une session',
          description: 'Cliquez sur le bouton "Inscription" pour ouvrir un formulaire modal avec les champs suivants :',
          details: [
            '🆔 ID Client (désactivé - automatique)',
            '📚 ID Formation (désactivé - automatique)',
            '📖 Nom de la formation (désactivé - automatique)',
            '📅 Session (sélectionnez la session souhaitée)'
          ],
          tip: '💡 Astuce : Les sessions disponibles sont celles ouvertes pour la formation achetée.'
        }
      ]
    },
    {
      id: 'factures-placement',
      icon: faHandshake,
      title: '1.9 Factures Placement',
      color: '#f59e0b',
      intro: 'La section Factures Placement permet de gérer les factures liées aux placements d\'agents dans les entreprises.',
      steps: [
        {
          step: 'a',
          title: 'Accès aux placements',
          description: 'Cliquez sur "Factures Placement" dans le menu latéral pour accéder à la liste des offres contenant des placements.',
          tip: '💡 Astuce : Un placement correspond à un agent positionné dans une entreprise.'
        },
        {
          step: 'b',
          title: 'Consultation des placements',
          description: 'Cliquez sur l\'onglet "Tous les placements" pour visualiser toutes les offres avec des placements d\'agents.',
          tip: '💡 Astuce : Les informations incluent le nombre d\'agents et les montants.'
        },
        {
          step: 'c',
          title: 'Création de la facture',
          description: 'Cliquez sur le bouton "Facture" pour ouvrir un modal affichant les informations du placement :',
          details: [
            '🏢 Entreprise concernée',
            '👥 Nombre d\'agents placés',
            '💰 Montants des prestations'
          ],
          tip: '💡 Astuce : Vérifiez tous les montants avant de valider la facture.'
        },
        {
          step: 'd',
          title: 'Enregistrement de la facture',
          description: 'Cliquez sur le bouton "Enregistrer" pour créer la facture définitive.',
          tip: '💡 Astuce : La facture est automatiquement numérotée et datée.'
        }
      ]
    },
    {
      id: 'factures-placement-list',
      icon: faFileInvoice,
      title: '1.10 Liste des Factures Placement',
      color: '#3b82f6',
      intro: 'Cette section permet de gérer les factures de placement émises et de créditer la caisse lors du paiement.',
      steps: [
        {
          step: 'a',
          title: 'Accès aux factures',
          description: 'Cliquez sur l\'onglet "Factures" dans le menu horizontal pour accéder à toutes les factures de placement.',
          tip: '💡 Astuce : Les factures sont classées par date d\'émission.'
        },
        {
          step: 'b',
          title: 'Consultation d\'une facture',
          description: 'Cliquez sur "Voir la facture" pour ouvrir un modal et télécharger la facture en PDF.',
          tip: '💡 Astuce : Le PDF est prêt à être envoyé à l\'entreprise cliente.'
        },
        {
          step: 'c',
          title: 'Validation du paiement',
          description: 'Sélectionnez le statut "Payée" pour la facture afin de créditer automatiquement la caisse.',
          warning: '⚠️ Important : Ce statut déclenche automatiquement un mouvement d\'entrée dans la caisse.'
        }
      ]
    }
  ];

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES – ADMIN TUTO COMPTABLE & CAISSE
           ============================================================ */
        :root {
          --bg-gradient: radial-gradient(circle at 10% 20%, #0f172a, #020617);
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e2e8f0;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --accent-blue: #3b82f6;
          --accent-green: #10b981;
          --accent-amber: #f59e0b;
          --accent-purple: #8b5cf6;
          --accent-red: #ef4444;
          --accent-cyan: #06b6d4;
          --accent-pink: #ec4899;
          --radius: 20px;
          --shadow: 0 20px 60px rgba(0,0,0,0.6);
        }

        .tuto-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 2rem 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        .tuto-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .tuto-header .back-link {
          color: var(--text-secondary);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          transition: 0.2s;
          font-size: 0.9rem;
        }
        .tuto-header .back-link:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }

        .tuto-header h1 {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          background: linear-gradient(135deg, #60a5fa, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .tuto-header .subtitle {
          color: var(--text-secondary);
          font-size: clamp(0.9rem, 1.2vw, 1.1rem);
          margin-top: 0.2rem;
        }

        .tuto-intro {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          padding: 1.5rem 2rem;
          margin-bottom: 2rem;
        }
        .tuto-intro .badge-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .tuto-intro .badge-roles span {
          padding: 0.25rem 0.8rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-secondary);
        }
        .tuto-intro .badge-roles .admin { background: rgba(244,63,94,0.15); color: #f43f5e; border-color: rgba(244,63,94,0.15); }
        .tuto-intro .badge-roles .dsi { background: rgba(6,182,212,0.15); color: #06b6d4; border-color: rgba(6,182,212,0.15); }
        .tuto-intro .badge-roles .comptable { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.15); }
        .tuto-intro .badge-roles .caisse { background: rgba(245,158,11,0.15); color: #f59e0b; border-color: rgba(245,158,11,0.15); }

        .section-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          margin-bottom: 1.2rem;
          overflow: hidden;
          transition: 0.3s;
        }
        .section-card:hover {
          border-color: rgba(255,255,255,0.08);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.2rem 1.8rem;
          cursor: pointer;
          transition: 0.2s;
          background: rgba(255,255,255,0.01);
          user-select: none;
        }
        .section-header:hover {
          background: rgba(255,255,255,0.03);
        }
        .section-header .left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .section-header .left .icon {
          font-size: 1.5rem;
          width: 36px;
          text-align: center;
        }
        .section-header .left .title {
          font-size: clamp(1.1rem, 1.4vw, 1.3rem);
          font-weight: 600;
        }
        .section-header .left .badge {
          font-size: 0.65rem;
          padding: 0.15rem 0.6rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          color: var(--text-muted);
        }
        .section-header .toggle-icon {
          color: var(--text-muted);
          transition: transform 0.3s;
          font-size: 0.9rem;
        }
        .section-header .toggle-icon.open {
          transform: rotate(180deg);
        }

        .section-content {
          padding: 0 1.8rem 1.8rem 1.8rem;
          border-top: 1px solid rgba(255,255,255,0.03);
          animation: slideDown 0.3s ease;
        }

        .section-intro {
          padding: 0.8rem 0 1rem 0;
          color: var(--text-secondary);
          font-style: italic;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .section-intro strong {
          color: var(--text-primary);
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .step-item {
          display: flex;
          gap: 1rem;
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .step-item:last-child {
          border-bottom: none;
        }
        .step-item .step-number {
          font-weight: 700;
          font-size: 1.1rem;
          min-width: 28px;
          color: var(--text-muted);
        }
        .step-item .step-content {
          flex: 1;
        }
        .step-item .step-content .step-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.2rem;
        }
        .step-item .step-content .step-desc {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }
        .step-item .step-content .step-details {
          margin-top: 0.4rem;
          padding-left: 1.2rem;
        }
        .step-item .step-content .step-details li {
          color: var(--text-secondary);
          font-size: 0.9rem;
          list-style: none;
          padding: 0.1rem 0;
        }
        .step-item .step-content .step-details li::before {
          content: '• ';
          color: var(--accent-blue);
        }
        .step-item .step-content .step-tip {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(59,130,246,0.08);
          border-left: 3px solid var(--accent-blue);
          border-radius: 6px;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .step-item .step-content .step-warning {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(239,68,68,0.08);
          border-left: 3px solid var(--accent-red);
          border-radius: 6px;
          font-size: 0.9rem;
          color: #fca5a5;
        }

        .footer-tuto {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .tuto-container { padding: 1.2rem; }
          .tuto-header { flex-direction: column; align-items: flex-start; }
          .section-header { padding: 1rem 1.2rem; flex-wrap: wrap; gap: 0.5rem; }
          .section-content { padding: 0 1.2rem 1.2rem 1.2rem; }
          .step-item { flex-direction: column; gap: 0.3rem; }
          .tuto-intro { padding: 1rem; }
        }
      `}</style>

      <div className="tuto-container">
        {/* ===== EN-TÊTE ===== */}
        <div className="tuto-header">
          <a href="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Retour
          </a>
          <div>
            <h1>💰 Tutoriel Comptable & Caisse</h1>
            <div className="subtitle">Bisenter ERP – Guide complet pour la gestion financière</div>
          </div>
        </div>

        {/* ===== INTRODUCTION ===== */}
        <div className="tuto-intro">
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Ce tutoriel couvre l'ensemble des fonctionnalités du module <strong style={{ color: '#fff' }}>Comptable & Caisse</strong> 
            de l'application Bisenter ERP. Il est destiné aux <strong style={{ color: '#fff' }}>Super Administrateurs</strong>, 
            aux <strong style={{ color: '#fff' }}>DSI</strong>, aux <strong style={{ color: '#fff' }}>Comptables</strong> 
            et au personnel de <strong style={{ color: '#fff' }}>Caisse</strong>.
          </p>
          <div className="badge-roles">
            <span className="admin"><FontAwesomeIcon icon={faUserShield} /> Super Administrateur</span>
            <span className="dsi"><FontAwesomeIcon icon={faUser} /> DSI</span>
            <span className="comptable"><FontAwesomeIcon icon={faMoneyBillWave} /> Comptable</span>
            <span className="caisse"><FontAwesomeIcon icon={faWallet} /> Caisse</span>
          </div>
        </div>

        {/* ===== SECTIONS ===== */}
        {sections.map((section) => (
          <div key={section.id} className="section-card">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <div className="left">
                <span className="icon" style={{ color: section.color }}>
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <span className="title">{section.title}</span>
                <span className="badge">{section.steps.length} étapes</span>
              </div>
              <FontAwesomeIcon
                icon={expandedSection === section.id ? faChevronDown : faChevronRight}
                className={`toggle-icon ${expandedSection === section.id ? 'open' : ''}`}
              />
            </div>

            {expandedSection === section.id && (
              <div className="section-content">
                <div className="section-intro">
                  <strong>À lire :</strong> {section.intro}
                </div>
                {section.steps.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">{step.step}.</div>
                    <div className="step-content">
                      <div className="step-title">{step.title}</div>
                      <div className="step-desc">{step.description}</div>
                      {step.details && (
                        <ul className="step-details">
                          {step.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      )}
                      {step.tip && (
                        <div className="step-tip">
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: 'var(--accent-blue)' }} />
                          {step.tip}
                        </div>
                      )}
                      {step.warning && (
                        <div className="step-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: 'var(--accent-red)' }} />
                          {step.warning}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* ===== NOTES IMPORTANTES ===== */}
        <div className="glass-card" style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: 'var(--radius)',
          padding: '1.5rem',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '0.8rem' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: 'var(--accent-amber)', marginRight: '0.5rem' }} />
            Notes importantes
          </h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.2rem' }}>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#fff' }}>Dépenses :</strong> Toute dépense enregistrée crée automatiquement un mouvement de sortie dans la caisse.
            </li>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#fff' }}>Paiements d'agents :</strong> Tout paiement d'employé enregistré crée un mouvement de sortie dans la caisse.
            </li>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#fff' }}>Ventes formations :</strong> Toute vente de formation enregistrée crée un mouvement d'entrée dans la caisse.
            </li>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#fff' }}>Factures placement :</strong> Toute facture dont le statut est "Payée" crée un mouvement d'entrée dans la caisse.
            </li>
          </ul>
        </div>

        {/* ===== PIED DE PAGE ===== */}
        <div className="footer-tuto">
          <p>
            <strong>Besoin d'aide ?</strong> Contactez le support technique à <span style={{ color: 'var(--accent-blue)' }}>support@bisenter.com</span>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Bisenter – Tous droits réservés.
          </p>
        </div>
      </div>
    </>
  );
};

// Ajout de l'icône ExclamationTriangle manquante
const faExclamationTriangle = {
  prefix: 'fas',
  iconName: 'exclamation-triangle',
  icon: [576, 512, [], 'f071', 'M569.5 440.4L320.2 40.6c-14.3-25.3-50.1-25.3-64.4 0L6.5 440.4c-14.3 25.3 3.6 57.1 32.2 57.1h498.6c28.5 0 46.5-31.8 32.2-57.1zM288 352c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm0-112c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32s32 14.3 32 32v48c0 17.7-14.3 32-32 32z']
};

export default AdminTutoComptable;